import { Collapsible } from '@/components/Collapsible';
import ContextualBar from '@/components/ContextualBar';
import CriticalBanner from '@/components/CriticalBanner';
import CustomCard from '@/components/customCard';
import GridLayoutPreview from '@/components/GridLayoutPreview';
import Layout from '@/components/layout';
import PreviewThankyouOrder from '@/components/Preview/PreviewThankyouOrder';
import UpdateTextStyle from '@/components/UpdateTextStyle';
import { stickyTop } from '@/constants';
import { BannerStatus, TextAppearanceKeys, TextEmphasisKeys, TextSizeKeys, ThankyouDisplayModes } from '@/constants/enum';
import { bannerStatusCards } from '@/constants/options';
import { PATH } from '@/constants/path';
import useToast from '@/hooks/useToast';
import BannerStatusCard from '@/pages/Checkout/components/BannerStatusCard';
import { AppearanceStyled } from '@/pages/Checkout/EditAppearance/styled';
import { apiCaller } from '@/redux/query';
import { isSkipApiSelector } from '@/redux/slice/auth.slice';
import { IResponseApi } from '@/types/apis/response';
import { Checkbox, InlineGrid, RadioButton, TextField } from '@shopify/polaris';
import isEqual from 'lodash/isEqual';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import useHandleContextual from '@/hooks/useHandleContextureBar';
import appbarSlice, { isShowContextualBarSelector } from '@/redux/slice/appbar.slice';
import { useCustomUserIdClarity } from '@/hooks/useCustomUserIdClarity';
import ModalConfirmLeavePage from '@/components/ModalConfirmLeavePage';

interface IProps {}

export default function EditAppearance(props: IProps) {
  useCustomUserIdClarity('Checkout appearance');
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { show } = useToast();
  const contextualBar = useHandleContextual();
  const isShowContextualBar = useSelector(isShowContextualBarSelector);
  const isSkip = useSelector(isSkipApiSelector);
  const { data, isLoading } = apiCaller.useGetCheckoutAppearanceQuery(undefined, { skip: isSkip });
  const [update, { isLoading: isLoadingUpdate }] = apiCaller.useUpdateCheckoutAppearanceMutation();
  const initialState: IResponseApi.ICheckoutAppearance = useMemo(() => {
    const dataAppearance = data?.data;
    return {
      displayMode: dataAppearance?.displayMode || ThankyouDisplayModes.banner,
      title: dataAppearance?.title || 'Delivery estimates',
      status: dataAppearance?.status || BannerStatus.info,
      textSize: dataAppearance?.textSize || TextSizeKeys.base,
      textColor: dataAppearance?.textColor || TextAppearanceKeys.base,
      textEmphasis: dataAppearance?.textEmphasis || TextEmphasisKeys.base,
      collapsible: typeof dataAppearance?.collapsible === 'boolean' ? dataAppearance?.collapsible : false,
      onDismiss: typeof dataAppearance?.onDismiss === 'boolean' ? dataAppearance?.onDismiss : false,
      labelText: dataAppearance?.labelText || 'Estimated between',
      isShowShippingMethodName:
        typeof dataAppearance?.isShowShippingMethodName === 'boolean' ? dataAppearance?.isShowShippingMethodName : true,
      isEnableCartLine: typeof dataAppearance?.isEnableCartLine === 'boolean' ? dataAppearance?.isEnableCartLine : false,
      isAddETAToAdditionalDetails: false,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(data)]);
  const [state, setState] = useState<IResponseApi.ICheckoutAppearance>(initialState);
  const [errorTitle, setErrorTitle] = useState('');
  const [error, setError] = useState<{ status: boolean; msg: string }>({ status: false, msg: '' });
  const [openModalConfirmLeave, setOpenModalConfirmLeave] = useState(false);

  useEffect(() => {
    setState(initialState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(initialState)]);

  const displayModes = useMemo(() => {
    return [
      { label: 'Banner', value: ThankyouDisplayModes.banner },
      { label: 'Plain text', value: ThankyouDisplayModes.plain },
    ];
  }, []);

  useEffect(() => {
    const temp = !isEqual(state, initialState);
    dispatch(appbarSlice.actions.handleUpdateContextualBarState({ isShowContextualBar: temp }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(initialState), JSON.stringify(state)]);

  const handleUpdateState = useCallback(
    (key: keyof IResponseApi.ICheckoutAppearance) =>
      (
        value:
          | string
          | string[]
          | boolean
          | ThankyouDisplayModes
          | BannerStatus
          | TextSizeKeys
          | TextAppearanceKeys
          | TextEmphasisKeys
          | ThankyouDisplayModes,
      ) => {
        setState((prev) => ({ ...prev, [key]: value }));
        if (error.status) setError({ status: false, msg: '' });
      },
    [error.status],
  );

  const handleDiscard = () => {
    setState(initialState);
    errorTitle && setErrorTitle('');
  };

  const handleSave = async () => {
    if (isSkip) return;
    try {
      const res = await update({ ...state }).unwrap();
      if (res.code === 200) {
        show('Saved');
      }
      navigate(PATH.CHECKOUT_EXTENSION);
    } catch (error) {
      //@ts-ignore:next-line
      setError({ status: true, msg: error?.data?.message?.toString() });
    }
  };

  const handleClickBackAction = useCallback(() => {
    dispatch(appbarSlice.actions.handleUpdateContextualBarState({ isShowContextualBar: false }));
    navigate(PATH.CHECKOUT_EXTENSION);
    contextualBar?.hide();
  }, [contextualBar, dispatch, navigate]);

  return (
    <Layout
      title="Edit banner appearance"
      backAction={{
        onAction: () => {
          isShowContextualBar ? setOpenModalConfirmLeave(true) : handleClickBackAction();
        },
      }}
      headers={
        <ContextualBar
          isVisible={isShowContextualBar}
          saveAction={handleSave}
          loadingSave={isLoading || isLoadingUpdate}
          isDisabled={!!errorTitle}
          discardAction={handleDiscard}
        />
      }
    >
      <GridLayoutPreview>
        <AppearanceStyled className="d-flex flex-column gap-12">
          {error.status && <CriticalBanner isVisible={error.status}>{error.msg}</CriticalBanner>}
          <CustomCard title={'Display mode'}>
            <div className="d-flex gap-12 mt-8">
              {displayModes.map((item) => {
                return (
                  <RadioButton
                    disabled={isLoading}
                    key={item.value}
                    label={item.label}
                    checked={state.displayMode === item.value}
                    id={item.value.toString()}
                    name={item.value.toString()}
                    onChange={() => {
                      handleUpdateState('displayMode')(item.value);
                    }}
                  />
                );
              })}
            </div>
          </CustomCard>

          {state.displayMode === ThankyouDisplayModes.banner ? (
            <>
              <CustomCard title={'Title'} required>
                <div className="mt-8">
                  <TextField
                    value={state.title}
                    maxLength={255}
                    showCharacterCount
                    onChange={(value) => {
                      handleUpdateState('title')(value);
                      if (errorTitle) setErrorTitle('');
                    }}
                    label=""
                    labelHidden
                    autoComplete="off"
                    error={errorTitle}
                    onBlur={() => {
                      if (!state.title) setErrorTitle('This field is required');
                    }}
                  />
                </div>
              </CustomCard>

              <CustomCard title={'Banner Status'}>
                <InlineGrid columns={{ xs: 1, sm: 2, md: 4 }} gap="300">
                  {bannerStatusCards.map((card) => (
                    <BannerStatusCard
                      key={card.value}
                      title={card.label}
                      backgroundColor={card.backgroundColor}
                      contentColor={card.contentColor}
                      icon={card.icon}
                      onClick={() => handleUpdateState('status')(card.value)}
                      selected={state.status === card.value}
                    />
                  ))}
                </InlineGrid>
              </CustomCard>

              <CustomCard title={'Banner actions'}>
                <div className="d-flex gap-12 mt-8">
                  <Checkbox label="Close button" checked={state.onDismiss} onChange={handleUpdateState('onDismiss')} />

                  <Checkbox label="Collapsible button" checked={state.collapsible} onChange={handleUpdateState('collapsible')} />
                </div>
              </CustomCard>
            </>
          ) : null}
          <Collapsible title="Message text style">
            <UpdateTextStyle
              changeTextAppearance={handleUpdateState('textColor')}
              changeTextEmphasis={handleUpdateState('textEmphasis')}
              changeTextSize={handleUpdateState('textSize')}
              textAppearance={state.textColor}
              textEmphasis={state.textEmphasis}
              textSize={state.textSize}
              title="Message"
            />
          </Collapsible>
        </AppearanceStyled>
        <PreviewThankyouOrder dataAppearance={state} displayMode={state.displayMode} top={stickyTop} />
      </GridLayoutPreview>
      <ModalConfirmLeavePage
        open={openModalConfirmLeave}
        onClose={() => setOpenModalConfirmLeave(false)}
        onConfirm={handleClickBackAction}
      />
    </Layout>
  );
}
